import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { countries } from '../utils/countries';
import './Rating.css';

const Rating = ({ onBack, userAccounts, importAccounts = [], isSuperUser, companySlug }) => {
    const [direction, setDirection] = useState('export'); // 'export' or 'import'
    const [origin, setOrigin] = useState({
        city: 'Sialkot',
        state: 'Punjab',
        country: 'PK',
        postalCode: '51310'
    });

    // Clear inputs and results when direction changes
    useEffect(() => {
        setDestCountrySearch('');
        setDestCountry(null);
        setDestAddress({ city: '', state: '', postalCode: '' });
        setExpandedAccountId(null);
        setAccountRates({});
        setAccountErrors({});
        setAccountDebugSteps({});
    }, [direction]);

    const [destCountrySearch, setDestCountrySearch] = useState('');
    const [showCountryResults, setShowCountryResults] = useState(false);
    const [destCountry, setDestCountry] = useState(null);

    const [destAddress, setDestAddress] = useState({
        city: '',
        state: '',
        postalCode: ''
    });

    const [packages, setPackages] = useState([
        { id: 1, weight: '', length: '', width: '', height: '', weightUnit: 'KGS', dimUnit: 'CM', additionalHandling: false }
    ]);

    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const [debugSteps, setDebugSteps] = useState(null);
    const [expandedAccountId, setExpandedAccountId] = useState(null);
    const [accountRates, setAccountRates] = useState({});
    const [loadingAccounts, setLoadingAccounts] = useState({});
    const [accountErrors, setAccountErrors] = useState({});
    const [accountDebugSteps, setAccountDebugSteps] = useState({});

    // Filter countries based on search
    const filteredCountries = useMemo(() => {
        if (!destCountrySearch) return [];
        return countries.filter(c =>
            c.name.toLowerCase().includes(destCountrySearch.toLowerCase()) ||
            c.code.toLowerCase().includes(destCountrySearch.toLowerCase())
        ).slice(0, 10);
    }, [destCountrySearch]);

    const [accounts, setAccounts] = useState([]);
    const [accountComments, setAccountComments] = useState({});
    const [selectedAccount, setSelectedAccount] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('NON-DOCUMENTS');

    // Filter accounts based on user permissions and direction (export vs import)
    const filteredAccounts = useMemo(() => {
        if (!accounts || accounts.length === 0) return [];
        
        // Super users can see all accounts
        if (isSuperUser) {
            return accounts;
        }
        
        if (direction === 'import') {
            if (importAccounts && importAccounts.includes('all')) return accounts;
            if (importAccounts && importAccounts.length > 0) {
                return accounts.filter(acc => importAccounts.includes(acc.account));
            }
        } else {
            if (userAccounts && userAccounts.includes('all')) return accounts;
            if (userAccounts && userAccounts.length > 0) {
                return accounts.filter(acc => userAccounts.includes(acc.account));
            }
        }
        
        // No accounts assigned to user for this direction
        return [];
    }, [accounts, userAccounts, importAccounts, isSuperUser, direction]);

    const productLabels = {
        'NON-DOCUMENTS': 'Non-Documents',
        'DOCUMENTS': 'Documents',
        'LETTERS': 'Letters'
    };

    // Fetch available accounts from settings
    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                let apiUrl;
                if (direction === 'import') {
                    apiUrl = process.env.NODE_ENV === 'development'
                        ? 'http://localhost:5001/api/settings/import-tariffs'
                        : '/api/settings/import-tariffs';
                } else {
                    if (companySlug) {
                        // Company user: fetch from company-scoped endpoint
                        apiUrl = process.env.NODE_ENV === 'development'
                            ? `http://localhost:5001/api/companies/${companySlug}/tariffs`
                            : `/api/companies/${companySlug}/tariffs`;
                    } else {
                        // Global super user: fetch from global endpoint
                        apiUrl = process.env.NODE_ENV === 'development'
                            ? 'http://localhost:5001/api/settings/tariffs'
                            : '/api/settings/tariffs';
                    }
                }
                const res = await axios.get(apiUrl);
                if (res.data.success) {
                    setAccounts(res.data.tariffs);
                    
                    // Extract comments
                    const comments = {};
                    res.data.tariffs.forEach(t => {
                        if (t.comment) {
                            comments[t.account] = t.comment;
                        }
                    });
                    setAccountComments(comments);

                    if (res.data.tariffs.length > 0) {
                        setSelectedAccount(res.data.tariffs[0].account);
                    } else {
                        setSelectedAccount('');
                    }
                }
            } catch (err) {
                console.error('Failed to load accounts');
            }
        };
        fetchAccounts();
    }, [companySlug, direction]);

    const addPackage = () => {
        if (packages.length < 30) {
            setPackages([...packages, {
                id: Date.now(),
                weight: '',
                length: '',
                width: '',
                height: '',
                weightUnit: 'KGS',
                dimUnit: 'CM',
                additionalHandling: false
            }]);
        }
    };

    const removePackage = (id) => {
        if (packages.length > 1) {
            setPackages(packages.filter(p => p.id !== id));
        }
    };

    const handlePackageChange = (id, field, value) => {
        setPackages(packages.map(p => p.id === id ? { ...p, [field]: value } : p));
    };

    const calculateVolumetricWeight = (p) => {
        if (!p.length || !p.width || !p.height) return 0;
        const volWeight = (parseFloat(p.length) * parseFloat(p.width) * parseFloat(p.height)) / 5000;
        return parseFloat(volWeight.toFixed(2));
    };

    const getBillableWeight = (p) => {
        const actual = parseFloat(p.weight) || 0;
        const volumetric = calculateVolumetricWeight(p);
        return Math.max(actual, volumetric);
    };

    const handleGetRates = async (e, accountToRate = null) => {
        e?.preventDefault?.();

        if (!destCountry) {
            setError('Please select a country');
            return;
        }

        // Build base payload
        const basePayload = {
            origin: direction === 'export' ? origin : {
                city: destAddress.city,
                state: destAddress.state,
                postalCode: destAddress.postalCode,
                country: destCountry.code
            },
            destination: direction === 'export' ? {
                ...destAddress,
                country: destCountry.code
            } : {
                city: origin.city,
                state: origin.state,
                postalCode: origin.postalCode,
                country: 'PK'
            },
            isImport: direction === 'import',
            product: selectedProduct,
            packages: packages.map(p => ({
                weight: p.weight,
                billableWeight: getBillableWeight(p),
                length: p.length,
                width: p.width,
                height: p.height,
                weightUnit: p.weightUnit,
                dimUnit: p.dimUnit,
                declaredValue: p.declaredValue || 0,
                additionalHandling: !!p.additionalHandling
            })),
            totalDeclaredValue: packages.reduce((sum, p) => sum + (parseFloat(p.declaredValue) || 0), 0)
        };

        // If no specific account provided, fetch rates for ALL accounts
        if (!accountToRate) {
            if (accounts.length === 0) {
                setError('No accounts available');
                return;
            }
            console.log('[Rating] --- Initiating Rate Query for ALL Accounts:', accounts.length);
            setLoading(true);
            setError(null);

            setAccountErrors({});
            setAccountDebugSteps({});
            setAccountRates({});

            const ratePromises = filteredAccounts.map(acc =>
                fetchRateForAccount(acc.account, basePayload)
            );

            try {
                await Promise.all(ratePromises);
            } catch (err) {
                console.error('Error fetching rates for all accounts:', err);
            } finally {
                setLoading(false);
            }
            return;
        }

        // Single account logic
        const accountForRate = accountToRate;
        console.log('[Rating] --- Initiating Rate Query for Account:', accountForRate);

        setLoadingAccounts(prev => ({ ...prev, [accountForRate]: true }));
        setAccountErrors(prev => ({ ...prev, [accountForRate]: null }));
        setAccountDebugSteps(prev => ({ ...prev, [accountForRate]: null }));

        const payload = {
            ...basePayload,
            account: accountForRate
        };

        try {
            const apiUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api/rating' : '/api/rating';
            const res = await axios.post(apiUrl, payload);
            if (res.data.success) {
                setAccountRates(prev => ({ ...prev, [accountForRate]: res.data.breakdown }));
                setAccountErrors(prev => ({ ...prev, [accountForRate]: null }));
                setAccountDebugSteps(prev => ({ ...prev, [accountForRate]: null }));
                setExpandedAccountId(accountForRate);
                setSelectedAccount(accountForRate);
                setError(null);
            } else {
                const errorMsg = res.data.message || 'Failed to fetch rates';
                const debugSteps = res.data.debugSteps || null;
                setAccountErrors(prev => ({ ...prev, [accountForRate]: errorMsg }));
                setAccountDebugSteps(prev => ({ ...prev, [accountForRate]: debugSteps }));
                setExpandedAccountId(accountForRate);
            }
        } catch (err) {
            let errorMsg = 'Error connecting to rating service';
            let steps = null;

            if (err.response?.status === 404) {
                errorMsg = err.response?.data?.message || '⚠️ Rates not available for selected product/weight combination';
                steps = err.response?.data?.debugSteps || null;
            } else if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
                steps = err.response?.data?.debugSteps || null;
            } else if (err.message) {
                errorMsg = err.message;
            }

            setAccountErrors(prev => ({ ...prev, [accountForRate]: errorMsg }));
            setAccountDebugSteps(prev => ({ ...prev, [accountForRate]: steps }));
            setExpandedAccountId(accountForRate);
        } finally {
            setLoadingAccounts(prev => ({ ...prev, [accountForRate]: false }));
        }
    };

    const fetchRateForAccount = async (accountNum, payload) => {
        try {
            const apiUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api/rating' : '/api/rating';
            const res = await axios.post(apiUrl, { ...payload, account: accountNum });

            if (res.data.success) {
                setAccountRates(prev => ({ ...prev, [accountNum]: res.data.breakdown }));
                setAccountErrors(prev => ({ ...prev, [accountNum]: null }));
                setAccountDebugSteps(prev => ({ ...prev, [accountNum]: null }));
            } else {
                const errorMsg = res.data.message || 'Failed to fetch rates';
                const debugSteps = res.data.debugSteps || null;
                setAccountErrors(prev => ({ ...prev, [accountNum]: errorMsg }));
                setAccountDebugSteps(prev => ({ ...prev, [accountNum]: debugSteps }));
            }
        } catch (err) {
            let errorMsg = 'Error connecting to rating service';
            let steps = null;

            if (err.response?.status === 404) {
                errorMsg = err.response?.data?.message || '⚠️ Rates not available for selected product/weight combination';
                steps = err.response?.data?.debugSteps || null;
            } else if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
                steps = err.response?.data?.debugSteps || null;
            } else if (err.message) {
                errorMsg = err.message;
            }

            setAccountErrors(prev => ({ ...prev, [accountNum]: errorMsg }));
            setAccountDebugSteps(prev => ({ ...prev, [accountNum]: steps }));
        }
    };

    return (
        <div className="rating-container premium-suite">
            <div className="rating-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button className="back-btn" onClick={onBack}>← Back</button>
                    <h2>💰 UPS Rating Service</h2>
                </div>
                
                {/* Direction Switcher Tab */}
                <div className="direction-tabs" style={{ display: 'flex', background: '#edf2f7', padding: '3px', borderRadius: '6px', gap: '4px' }}>
                    <button
                        type="button"
                        onClick={() => setDirection('export')}
                        style={{
                            padding: '6px 12px',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            background: direction === 'export' ? '#3182ce' : 'transparent',
                            color: direction === 'export' ? '#fff' : '#4a5568'
                        }}
                    >
                        📤 Export (Outbound)
                    </button>
                    <button
                        type="button"
                        onClick={() => setDirection('import')}
                        style={{
                            padding: '6px 12px',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            background: direction === 'import' ? '#8b5cf6' : 'transparent',
                            color: direction === 'import' ? '#fff' : '#4a5568'
                        }}
                    >
                        📥 Import (Inbound)
                    </button>
                </div>
            </div>

            <div className="rating-card premium-card">
                <form className="rating-form" onSubmit={handleGetRates}>

                    {/* Header Info */}
                    <div className="form-section-header">
                        <h3>1. Shipment Details</h3>
                        <p>{direction === 'export' ? 'Configure origin tax and product classification' : 'Configure destination tax and product classification'}</p>
                    </div>

                    {/* Address Split - Rating Style */}
                    <div className="address-split-container" style={{ marginBottom: '25px' }}>
                        {/* PAKISTAN ADDRESS / BASIC INFO */}
                        <div className="address-column">
                            <h3>{direction === 'export' ? 'Origin & Product' : 'Destination & Product'}</h3>

                            <div className="field-row">
                                <div className="field-group">
                                    <label>{direction === 'export' ? 'Origin Country' : 'Destination Country'}</label>
                                    <input className="premium-input" type="text" value="Pakistan (PK)" readOnly style={{ backgroundColor: '#f7fafc' }} />
                                </div>
                            </div>

                            <div className="field-row">
                                <div className="field-group">
                                    <label>{direction === 'export' ? 'Origin State / GST' : 'Destination State / GST'}</label>
                                    <select
                                        className="premium-input"
                                        value={origin.state}
                                        onChange={(e) => setOrigin({ ...origin, state: e.target.value })}
                                        required
                                    >
                                        <option value="Punjab">16% GST (Punjab)</option>
                                        <option value="Sindh">15% GST (Sindh)</option>
                                        <option value="fixed">Fixed Tax (1000 PKR)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="field-row">
                                <div className="field-group">
                                    <label>Product Type</label>
                                    <select
                                        className="premium-input"
                                        value={selectedProduct}
                                        onChange={(e) => setSelectedProduct(e.target.value)}
                                    >
                                        <option value="NON-DOCUMENTS">Non-Documents</option>
                                        <option value="DOCUMENTS">Documents</option>
                                        <option value="LETTERS">Letters</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="address-divider"></div>

                        {/* Foreign Address Column */}
                        <div className="address-column premium-column">
                            <div className="column-header">
                                <span className="icon">{direction === 'export' ? '🛬' : '🛫'}</span>
                                <h3>{direction === 'export' ? 'Ship To (Destination)' : 'Ship From (Origin)'}</h3>
                            </div>
                            <div className="field-row">
                                <div className="field-group">
                                    <label>{direction === 'export' ? 'Search Destination Country *' : 'Search Origin Country *'}</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            className="premium-input"
                                            placeholder="Type country name..."
                                            value={destCountrySearch}
                                            onChange={(e) => {
                                                setDestCountrySearch(e.target.value);
                                                setShowCountryResults(true);
                                            }}
                                            onFocus={() => setShowCountryResults(true)}
                                            required={!destCountry}
                                        />
                                        {showCountryResults && filteredCountries.length > 0 && (
                                            <div className="country-results-dropdown-premium">
                                                {filteredCountries.map(c => (
                                                    <div
                                                        key={c.code}
                                                        className="country-result-item"
                                                        onClick={() => {
                                                            setDestCountry(c);
                                                            setDestCountrySearch(c.name);
                                                            setShowCountryResults(false);
                                                        }}
                                                    >
                                                        <span className="flag">{c.flag}</span>
                                                        <span className="name">{c.name}</span>
                                                        <span className="code">{c.code}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="field-row">
                                <div className="field-group">
                                    <label>City *</label>
                                    <input
                                        className="premium-input"
                                        placeholder={direction === 'export' ? 'Destination City' : 'Origin City'}
                                        value={destAddress.city}
                                        onChange={(e) => setDestAddress({ ...destAddress, city: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="city-zip-state-row">
                                <div className="field-group" style={{ flex: 2 }}>
                                    <label>State / Province</label>
                                    <input
                                        className="premium-input"
                                        placeholder="State code / name"
                                        value={destAddress.state}
                                        onChange={(e) => setDestAddress({ ...destAddress, state: e.target.value })}
                                    />
                                </div>
                                <div className="field-group">
                                    <label>Postal Code</label>
                                    <input
                                        className="premium-input"
                                        placeholder="Zip/Postal"
                                        value={destAddress.postalCode}
                                        onChange={(e) => setDestAddress({ ...destAddress, postalCode: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-section-header">
                        <h3>2. Product Selection</h3>
                        <p>Select what type of items you are shipping</p>
                    </div>

                    <div className="fc-service-grid-premium" style={{ marginBottom: '25px' }}>
                        {Object.entries(productLabels).map(([code, label]) => (
                            <div
                                key={code}
                                className={`fc-service-card-premium ${selectedProduct === code ? 'selected' : ''}`}
                                onClick={() => setSelectedProduct(code)}
                            >
                                <div className="svc-icon">{code === 'LETTERS' ? '✉️' : code === 'DOCUMENTS' ? '📄' : '📦'}</div>
                                <div className="svc-info">
                                    <div className="svc-label">{label}</div>
                                    <div className="svc-desc">{code === 'LETTERS' ? 'UPS Express Envelope' : code === 'DOCUMENTS' ? 'Commercial documents' : 'Goods and merchandise'}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="form-section-header">
                        <h3>3. Package Details</h3>
                        <p>Enter dimensions and weight for each piece</p>
                    </div>

                    <div className="premium-package-list">
                        {packages.map((pkg, index) => (
                            <div key={pkg.id} className="package-card-premium">
                                <div className="package-card-header">
                                    <span className="pkg-badge">Package #{index + 1}</span>
                                    {packages.length > 1 && (
                                        <button type="button" className="remove-pkg-btn" onClick={() => removePackage(pkg.id)}>✕</button>
                                    )}
                                </div>
                                <div className="package-grid-row">
                                    <div className="field-group">
                                        <label>Weight (kg)*</label>
                                        <input
                                            className="premium-input"
                                            type="number"
                                            step="0.01"
                                            value={pkg.weight}
                                            onChange={(e) => handlePackageChange(pkg.id, 'weight', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="field-group">
                                        <label>Value (USD)</label>
                                        <input
                                            className="premium-input"
                                            type="number"
                                            value={pkg.declaredValue || ''}
                                            onChange={(e) => handlePackageChange(pkg.id, 'declaredValue', e.target.value)}
                                        />
                                    </div>
                                    <div className="field-group">
                                        <label>L (cm)</label>
                                        <input
                                            className="premium-input"
                                            type="number"
                                            value={pkg.length}
                                            onChange={(e) => handlePackageChange(pkg.id, 'length', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="field-group">
                                        <label>W (cm)</label>
                                        <input
                                            className="premium-input"
                                            type="number"
                                            value={pkg.width}
                                            onChange={(e) => handlePackageChange(pkg.id, 'width', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="field-group">
                                        <label>H (cm)</label>
                                        <input
                                            className="premium-input"
                                            type="number"
                                            value={pkg.height}
                                            onChange={(e) => handlePackageChange(pkg.id, 'height', e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                                <div style={{ margin: '8px 0 4px 0', display: 'flex', alignItems: 'center' }}>
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: '#334155', cursor: 'pointer', fontWeight: 600 }}>
                                        <input
                                            type="checkbox"
                                            checked={!!pkg.additionalHandling}
                                            onChange={(e) => handlePackageChange(pkg.id, 'additionalHandling', e.target.checked)}
                                            style={{ width: '16px', height: '16px', accentColor: '#2563eb', cursor: 'pointer' }}
                                        />
                                        Additional Handling / Irregular Packaging (AHC)
                                    </label>
                                </div>
                                <div className="package-footer-info">
                                    <div className="info-pill">Volumetric: <strong>{calculateVolumetricWeight(pkg)} KGS</strong></div>
                                    <div className="info-pill highlight">Billable: <strong>{getBillableWeight(pkg)} KGS</strong></div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
                        {packages.length < 30 && (
                            <button type="button" className="add-pkg-btn-premium" onClick={addPackage}>
                                + Add Another Package
                            </button>
                        )}

                        <button type="submit" className="calculate-btn-premium" disabled={loading}>
                            {loading ? (
                                <><span className="spinner-small"></span> Calculating...</>
                            ) : (
                                '🔍 Get Estimated Rates'
                            )}
                        </button>
                    </div>
                </form>

                {/* RESULTS SIDEBAR - ACCOUNT CARDS */}
                <div className="rating-results">
                    {/* Account Cards Grid */}
                    {expandedAccountId === null && (
                        <div className="accounts-grid">
                            <div className="accounts-header">
                                <h3>💳 Select Account for Rates</h3>
                                <p>Click on an account card to view rates</p>
                            </div>
                            {filteredAccounts.length === 0 ? (
                                <div className="no-accounts">
                                    <p>No accounts available for your access level. Contact admin for permissions.</p>
                                </div>
                            ) : (
                                <div className="account-cards-container">
                                    {filteredAccounts.map(acc => {
                                        const accountRatesData = accountRates[acc.account];
                                        const isLoading = loadingAccounts[acc.account] || (loading && accounts.length > 1);
                                        const accountError = accountErrors[acc.account];
                                        return (
                                            <div
                                                key={acc.account}
                                                className={`account-card-small ${selectedAccount === acc.account ? 'selected' : ''} ${accountError ? 'error' : ''}`}
                                                onClick={() => {
                                                    if (accountRatesData || accountError) {
                                                        // If rates exist or error exists, expand to show details
                                                        setExpandedAccountId(acc.account);
                                                        setSelectedAccount(acc.account);
                                                    }
                                                }}
                                                style={{ cursor: (accountRatesData || accountError) ? 'pointer' : 'default' }}
                                            >
                                                {isLoading ? (
                                                    <div className="card-loading">
                                                        <div className="card-spinner"></div>
                                                        <p>Loading...</p>
                                                    </div>
                                                ) : accountError ? (
                                                    <div className="card-error">
                                                        <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>❌</div>
                                                        <div style={{ fontSize: '0.85rem', color: '#c53030', fontWeight: 'bold', marginBottom: '0.5rem' }}>Error</div>
                                                        <div style={{ fontSize: '0.75rem', color: '#742a2a' }}>
                                                            Click to view details
                                                        </div>
                                                    </div>
                                                ) : accountRatesData ? (
                                                    <>
                                                        <div className="card-header">
                                                            <span className="account-number">
                                                                {acc.account}
                                                                {accountComments[acc.account] && (
                                                                    <span style={{ marginLeft: '6px', background: '#fefcbf', color: '#b7791f', padding: '2px 6px', borderRadius: '4px', fontSize: '0.65rem' }}>
                                                                        {accountComments[acc.account]}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <span className="service-type">{acc.serviceType}</span>
                                                        </div>
                                                        <div className="card-total">
                                                            <div className="total-label">Grand Total</div>
                                                            <div className="total-amount">{accountRatesData.grandTotalPKR}</div>
                                                            <div className="total-currency">PKR</div>
                                                        </div>
                                                        <div className="card-click-hint">Click to expand</div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="card-header">
                                                            <span className="account-number">{acc.account}</span>
                                                            <span className="service-type">{acc.serviceType}</span>
                                                        </div>
                                                        <div className="card-placeholder" style={{ marginTop: '1rem', textAlign: 'center', color: '#cbd5e0', fontSize: '0.85rem' }}>
                                                            Use "Get Estimated Rates" button above
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Expanded Account Breakdown */}
                    {expandedAccountId !== null && (
                        <div className="account-expanded">
                            <button
                                className="collapse-btn"
                                onClick={() => {
                                    setExpandedAccountId(null);
                                    setSelectedAccount('');
                                    setResults(null);
                                }}
                            >
                                ← Back to Accounts
                            </button>

                            {loadingAccounts[expandedAccountId] ? (
                                <div className="loading-container">
                                    <div className="spinner"></div>
                                    <p>Analyzing rates...</p>
                                </div>
                            ) : accountErrors[expandedAccountId] ? (
                                <div className="error-container">
                                    <div className="error-box">{accountErrors[expandedAccountId]}</div>
                                    {accountDebugSteps[expandedAccountId] && accountDebugSteps[expandedAccountId].length > 0 && (
                                        <div style={{
                                            marginTop: '1rem',
                                            padding: '1rem',
                                            backgroundColor: '#f7fafc',
                                            border: '1px solid #cbd5e0',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem'
                                        }}>
                                            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#2d3748' }}>
                                                🔍 Tariff Lookup Details:
                                            </div>
                                            {accountDebugSteps[expandedAccountId].map((step, idx) => (
                                                <div key={idx} style={{
                                                    marginBottom: '0.75rem',
                                                    paddingLeft: '1rem',
                                                    borderLeft: `3px solid ${step.status === 'success' ? '#48bb78' :
                                                        step.status === 'success_with_note' ? '#ed8936' :
                                                            step.status === 'failed' ? '#f56565' : '#cbd5e0'
                                                        }`,
                                                    color: '#4a5568'
                                                }}>
                                                    <div style={{ fontWeight: 'bold' }}>
                                                        {step.status === 'success' ? '✓' : step.status === 'failed' ? '✗' : '▸'} Step {step.step}: {step.action}
                                                    </div>
                                                    {step.message && (
                                                        <div style={{ marginTop: '0.25rem', fontSize: '0.8rem' }}>
                                                            {step.message}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : accountRates[expandedAccountId] ? (
                                <div className="results-list">
                                    <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#1a202c', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                                        {expandedAccountId} - Charges Breakdown
                                    </h3>

                                    {accountRates[expandedAccountId].alerts && accountRates[expandedAccountId].alerts.length > 0 && (
                                        <div className="ups-alerts" style={{ marginBottom: '1rem' }}>
                                            {accountRates[expandedAccountId].alerts.map((alert, idx) => (
                                                <div key={idx} className="alert-item" style={{
                                                    backgroundColor: '#fffaf0',
                                                    border: '1px solid #feebc8',
                                                    padding: '0.5rem',
                                                    borderRadius: '4px',
                                                    fontSize: '0.8rem',
                                                    color: '#c05621',
                                                    marginBottom: '0.5rem',
                                                    display: 'flex',
                                                    alignItems: 'start',
                                                    gap: '8px'
                                                }}>
                                                    <span>⚠️</span>
                                                    <span><strong>UPS Info ({alert.code}):</strong> {alert.description}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="breakdown-total">
                                        <div className="total-item">
                                            <label>📦 Billing Weight</label>
                                            <span>{accountRates[expandedAccountId].totalWeight} KGS</span>
                                        </div>
                                        <div className="total-item highlight">
                                            <label>Total Charges (PKR)</label>
                                            <span>{accountRates[expandedAccountId].grandTotalPKR}</span>
                                        </div>
                                    </div>

                                    <div className="breakdown-grid">
                                        <div className="breakdown-row header">
                                            <span>Item</span>
                                            <span>USD</span>
                                            <span>PKR</span>
                                        </div>

                                        <div className="breakdown-row">
                                            <span className="item-label">📊 Freight (From Tariff)</span>
                                            <span>{accountRates[expandedAccountId].freightUSD}</span>
                                            <span>{accountRates[expandedAccountId].freightPKR}</span>
                                        </div>

                                        <div className="breakdown-row">
                                            <span className="item-label">SBC (Fixed)</span>
                                            <span>{accountRates[expandedAccountId].sbcUSD}</span>
                                            <span>{accountRates[expandedAccountId].sbcPKR}</span>
                                        </div>

                                        <div className="breakdown-row">
                                            <span className="item-label">IPF - International Processing Fee (Code 573)</span>
                                            <span>{accountRates[expandedAccountId].ipfUSD}</span>
                                            <span>{accountRates[expandedAccountId].ipfPKR}</span>
                                        </div>

                                        <div className="breakdown-row">
                                            <span className="item-label">Insurance (6.6 USD if &lt; 6600, else 1% of value)</span>
                                            <span>{accountRates[expandedAccountId].insuranceUSD}</span>
                                            <span>{accountRates[expandedAccountId].insurancePKR}</span>
                                        </div>

                                        <div className="breakdown-row">
                                            <span className="item-label">Delivery Surcharge (Code 190 from UPS)</span>
                                            <span>{accountRates[expandedAccountId].deliverySurchargeUSD}</span>
                                            <span>{accountRates[expandedAccountId].deliverySurchargePKR}</span>
                                        </div>

                                        <div className="breakdown-row">
                                            <span className="item-label">Large Package Surcharge (Code 377 from UPS)</span>
                                            <span>{accountRates[expandedAccountId].largePkgSurchargeUSD}</span>
                                            <span>{accountRates[expandedAccountId].largePkgSurchargePKR}</span>
                                        </div>

                                        <div className="breakdown-row">
                                            <span className="item-label">Additional Handling (Code 100 from UPS)</span>
                                            <span>{accountRates[expandedAccountId].additionalHandlingUSD}</span>
                                            <span>{accountRates[expandedAccountId].additionalHandlingPKR}</span>
                                        </div>

                                        {parseFloat(accountRates[expandedAccountId].peakSeasonLargePackageUSD || 0) > 0 && (
                                            <div className="breakdown-row">
                                                <span className="item-label">Peak Season Surcharge - LP (UPS Code 431)</span>
                                                <span>{accountRates[expandedAccountId].peakSeasonLargePackageUSD}</span>
                                                <span>{accountRates[expandedAccountId].peakSeasonLargePackagePKR}</span>
                                            </div>
                                        )}

                                        {parseFloat(accountRates[expandedAccountId].peakSeasonAdditionalHandlingUSD || 0) > 0 && (
                                            <div className="breakdown-row">
                                                <span className="item-label">Peak Season Surcharge - AH (UPS Code 432)</span>
                                                <span>{accountRates[expandedAccountId].peakSeasonAdditionalHandlingUSD}</span>
                                                <span>{accountRates[expandedAccountId].peakSeasonAdditionalHandlingPKR}</span>
                                            </div>
                                        )}

                                        <div className="breakdown-row highlight-sub">
                                            <span className="item-label">Fuel Surcharge ({accountRates[expandedAccountId].fuelUsed})</span>
                                            <span>{accountRates[expandedAccountId].fuelSurchargeUSD}</span>
                                            <span>{accountRates[expandedAccountId].fuelSurchargePKR}</span>
                                        </div>

                                        {(() => {
                                            console.log('[Rating Display] Peak Charges Debug:', {
                                                expandedAccountId: expandedAccountId,
                                                peakChargesUSD: accountRates[expandedAccountId]?.peakChargesUSD,
                                                peakChargesPKR: accountRates[expandedAccountId]?.peakChargesPKR,
                                                parseFloat: parseFloat(accountRates[expandedAccountId]?.peakChargesUSD || 0),
                                                condition: accountRates[expandedAccountId]?.peakChargesUSD && parseFloat(accountRates[expandedAccountId]?.peakChargesUSD) > 0
                                            });
                                            if (accountRates[expandedAccountId]?.peakChargesUSD && parseFloat(accountRates[expandedAccountId]?.peakChargesUSD) > 0) {
                                                return (
                                                    <div className="breakdown-row highlight-sub">
                                                        <span className="item-label">Peak Period Charges</span>
                                                        <span>{accountRates[expandedAccountId].peakChargesUSD}</span>
                                                        <span>{accountRates[expandedAccountId].peakChargesPKR}</span>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}

                                        {(() => {
                                            const specialUSD = accountRates[expandedAccountId]?.specialSurchargeUSD;
                                            const specialPKR = accountRates[expandedAccountId]?.specialSurchargePKR;
                                            const specialCountry = accountRates[expandedAccountId]?.specialSurchargeCountry;
                                            if (specialUSD && parseFloat(specialUSD) > 0 && specialCountry) {
                                                return (
                                                    <div className="breakdown-row highlight-sub" style={{ backgroundColor: 'rgba(79, 70, 229, 0.05)', borderLeft: '3px solid #4f46e5' }}>
                                                        <span className="item-label">🌍 Country Surcharge ({specialCountry})</span>
                                                        <span>{specialUSD}</span>
                                                        <span>{specialPKR}</span>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}

                                        <div className="breakdown-row highlight-sub" style={{ borderTop: '1px solid #e2e8f0', marginTop: '5px' }}>
                                            <span className="item-label">TOTAL FREIGHT CHARGES (BEFORE TAX)</span>
                                            <span>{accountRates[expandedAccountId].subtotalUSD}</span>
                                            <span>{accountRates[expandedAccountId].subtotalPKR}</span>
                                        </div>

                                        <div className="breakdown-row">
                                            <span className="item-label">{accountRates[expandedAccountId].taxPercentage || 'Sales Tax (GST 15%)'}</span>
                                            <span>{accountRates[expandedAccountId].salesTaxUSD}</span>
                                            <span>{accountRates[expandedAccountId].salesTaxPKR}</span>
                                        </div>

                                        <div className="breakdown-row grand-total">
                                            <span className="item-label">TOTAL FREIGHT CHARGES (WITH TAX)</span>
                                            <span>{accountRates[expandedAccountId].totalUSD}</span>
                                            <span>{accountRates[expandedAccountId].totalPKR}</span>
                                        </div>
                                    </div>

                                    <p style={{ fontSize: '0.7rem', color: '#a0aec0', marginTop: '1rem', textAlign: 'center' }}>
                                        * Base Currency: USD. Conversion Rate: {accountRates[expandedAccountId].rateUsed} PKR. Fuel Rate: {accountRates[expandedAccountId].fuelUsed}
                                    </p>
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* Original Results for non-card flow - hidden when using cards */}
                    {expandedAccountId === null && !accountRates[selectedAccount] && (
                        <div className="results-placeholder">
                            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M21 12V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19H12" strokeLinecap="round" />
                                <path d="M16 19L18 21L22 17" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M3 10H21" strokeLinecap="round" />
                                <path d="M7 15H8" strokeLinecap="round" />
                                <path d="M11 15H12" strokeLinecap="round" />
                            </svg>
                            <p>Select an account to view rates based on your custom tariffs.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Rating;
